import * as THREE from 'three';
import { CubeMesh } from './cube';
import type { SceneAssets } from './assets';
import { tween, easeOutCubic, easeInCubic } from './anim';
import { spawnFloatText } from './floattext';

const N = 10;
export const CELL = 1;

/** Manages the 10x10 board: the wooden slab, grid, hover highlight, and placed cubes. */
export class BoardView {
  group = new THREE.Group();
  private cubes = new Map<number, CubeMesh>();
  private hoverTiles: THREE.Mesh[] = [];
  private assets: SceneAssets;

  constructor(assets: SceneAssets) {
    this.assets = assets;
    this.buildSlab();
    if (assets.socketMap) this.buildSockets();
    else this.buildGrid();
    this.buildHoverPool();
  }

  static x(index: number): number {
    return index % N;
  }
  static z(index: number): number {
    return Math.floor(index / N);
  }
  static idx(x: number, z: number): number {
    return z * N + x;
  }

  /** Center of board cell (cx, cz) in world space, at height y. */
  cellToWorld(cx: number, cz: number, y = 0): THREE.Vector3 {
    return new THREE.Vector3((cx - (N - 1) / 2) * CELL, y, (cz - (N - 1) / 2) * CELL);
  }

  /** World point (on the board plane) -> nearest cell coords. */
  worldToCell(p: THREE.Vector3): { cx: number; cz: number } {
    return {
      cx: Math.round(p.x / CELL + (N - 1) / 2),
      cz: Math.round(p.z / CELL + (N - 1) / 2),
    };
  }

  private buildSlab(): void {
    const inner = N * CELL;
    const size = inner + 1.4;
    const woodMat = new THREE.MeshStandardMaterial({
      map: this.assets.boardBaseMap ?? this.assets.woodMap ?? null,
      color: this.assets.boardBaseMap || this.assets.woodMap ? 0xc8b48f : 0x4d3722,
      roughness: 0.78,
      metalness: 0.04,
    });
    const slab = new THREE.Mesh(new THREE.BoxGeometry(size, 0.5, size), woodMat);
    slab.position.y = -0.26;
    slab.receiveShadow = true;
    slab.castShadow = true;
    this.group.add(slab);

    // Recessed inner well (dark) so the sockets read as inset into the board.
    const well = new THREE.Mesh(
      new THREE.BoxGeometry(inner + 0.2, 0.06, inner + 0.2),
      new THREE.MeshStandardMaterial({ color: 0x241710, roughness: 0.9 }),
    );
    well.position.y = -0.02;
    well.receiveShadow = true;
    this.group.add(well);

    // Raised carved wooden rim framing the playfield.
    const rimMat = new THREE.MeshStandardMaterial({
      map: this.assets.boardBaseMap ?? null,
      color: this.assets.boardBaseMap ? 0xb09064 : 0x3a2917,
      roughness: 0.8,
      metalness: 0.05,
    });
    const rimH = 0.34;
    const outer = inner + 1.0;
    const thick = 0.5;
    const half = outer / 2;
    const segs: [number, number, number, number][] = [
      [0, half - thick / 2, outer, thick],
      [0, -(half - thick / 2), outer, thick],
      [half - thick / 2, 0, thick, outer],
      [-(half - thick / 2), 0, thick, outer],
    ];
    for (const [cx, cz, w, d] of segs) {
      const bar = new THREE.Mesh(new THREE.BoxGeometry(w, rimH, d), rimMat);
      bar.position.set(cx, rimH / 2 - 0.02, cz);
      bar.castShadow = true;
      bar.receiveShadow = true;
      this.group.add(bar);
    }
  }

  /** 10x10 recessed sockets using the original BoardSocket texture. */
  private buildSockets(): void {
    const geo = new THREE.PlaneGeometry(CELL, CELL);
    const mat = new THREE.MeshStandardMaterial({
      map: this.assets.socketMap!,
      roughness: 0.72,
      metalness: 0.03,
    });
    for (let z = 0; z < N; z++) {
      for (let x = 0; x < N; x++) {
        const tile = new THREE.Mesh(geo, mat);
        tile.rotation.x = -Math.PI / 2;
        const p = this.cellToWorld(x, z, 0.012);
        tile.position.set(p.x, 0.012, p.z);
        tile.receiveShadow = true;
        this.group.add(tile);
      }
    }
  }

  private buildGrid(): void {
    const pts: number[] = [];
    const half = (N / 2) * CELL;
    for (let i = 0; i <= N; i++) {
      const o = -half + i * CELL;
      pts.push(-half, 0.012, o, half, 0.012, o);
      pts.push(o, 0.012, -half, o, 0.012, half);
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));
    const mat = new THREE.LineBasicMaterial({ color: 0x1c130a, transparent: true, opacity: 0.5 });
    this.group.add(new THREE.LineSegments(geo, mat));
  }

  private buildHoverPool(): void {
    const geo = new THREE.PlaneGeometry(CELL * 0.94, CELL * 0.94);
    for (let i = 0; i < 9; i++) {
      const mat = new THREE.MeshBasicMaterial({ transparent: true, opacity: 0.0, depthWrite: false });
      const m = new THREE.Mesh(geo, mat);
      m.rotation.x = -Math.PI / 2;
      m.position.y = 0.02;
      m.visible = false;
      this.hoverTiles.push(m);
      this.group.add(m);
    }
  }

  showHover(cells: { x: number; z: number }[], anchorX: number, anchorZ: number, valid: boolean): void {
    const color = valid ? 0x6fcf6f : 0xd86a5a;
    for (let i = 0; i < this.hoverTiles.length; i++) {
      const tile = this.hoverTiles[i];
      if (i < cells.length) {
        const cx = anchorX + cells[i].x;
        const cz = anchorZ + cells[i].z;
        const p = this.cellToWorld(cx, cz, 0.02);
        tile.position.set(p.x, 0.02, p.z);
        (tile.material as THREE.MeshBasicMaterial).color.setHex(color);
        (tile.material as THREE.MeshBasicMaterial).opacity = 0.42;
        tile.visible = true;
      } else {
        tile.visible = false;
      }
    }
  }

  hideHover(): void {
    for (const t of this.hoverTiles) t.visible = false;
  }

  hasCube(index: number): boolean {
    return this.cubes.has(index);
  }

  addCube(index: number, value: number, drop = true): void {
    const cube = new CubeMesh(value, this.assets);
    const p = this.cellToWorld(BoardView.x(index), BoardView.z(index), 0.46);
    cube.group.position.copy(p);
    this.group.add(cube.group);
    this.cubes.set(index, cube);
    if (drop) {
      // Original (Cube.LoadInCube) snaps the cube into place instantly. Keep just a brief,
      // non-bouncy settle from a small height so it reads "snappy/precise", not "floaty".
      const targetY = p.y;
      cube.group.position.y = targetY + 0.8;
      tween(100, (t) => {
        cube.group.position.y = targetY + 0.8 * (1 - t);
      }, { ease: easeOutCubic });
    }
  }

  boostCube(index: number, value: number, amount: number): void {
    const cube = this.cubes.get(index);
    if (!cube) return;
    cube.setValue(value);
    // Original boosts the number with NO scale punch (CubeText pulse is commented out);
    // only the "+N" float text appears.
    const p = this.cellToWorld(BoardView.x(index), BoardView.z(index), 0.7);
    spawnFloatText(this.group, p, `+${amount}`, '#ffd27a');
  }

  removeCube(index: number, delay = 0): void {
    const cube = this.cubes.get(index);
    if (!cube) return;
    this.cubes.delete(index);
    const startY = cube.group.position.y;
    const p = this.cellToWorld(BoardView.x(index), BoardView.z(index), 0.7);
    tween(1, () => {}, { delay, onComplete: () => spawnFloatText(this.group, p, `+${cube.value}`, '#fff0d0') });
    tween(
      300,
      (t) => {
        cube.group.position.y = startY + 1.2 * t;
        cube.group.scale.setScalar(1 - t);
      },
      { delay, ease: easeInCubic, onComplete: () => cube.dispose() },
    );
  }

  /** Rebuild all cubes from a board array (no animation) — used on load / undo. */
  syncFromBoard(board: number[]): void {
    for (const cube of this.cubes.values()) cube.dispose();
    this.cubes.clear();
    for (let i = 0; i < board.length; i++) {
      if (board[i] > 0) this.addCube(i, board[i], false);
    }
  }

  clearAll(): void {
    for (const cube of this.cubes.values()) cube.dispose();
    this.cubes.clear();
    this.hideHover();
  }
}
